import { Link } from 'react-router-dom';
import {
  GlobeHemisphereWest,
  Scroll,
  VideoCamera,
  PenNib,
  Stamp,
  IdentificationCard,
  Heart,
  FileText,
  HouseLine,
  CalendarCheck,
  MapPin,
  Globe,
  Fingerprint,
  Phone,
  ArrowRight,
  UsersThree,
} from '@phosphor-icons/react';
import { BRAND, COVERAGE } from '../lib/brand';

export const ServicesPage = () => {
  // Slugs map to the individual detail pages built under /services/{slug}
  const serviceCategories = [
    {
      title: "Document & Apostille Services",
      description: "Documents that demand experience, discretion, and a Nashville-local advantage.",
      icon: Scroll,
      services: [
        {
          icon: GlobeHemisphereWest,
          name: "Apostille & International Certification",
          slug: "apostille",
          description: "Expedited Nashville-based apostille for birth certificates, marriage licenses, diplomas, FBI background checks, and corporate filings. Direct working relationship with the Tennessee Secretary of State means faster turnarounds for your international move, adoption, dual-citizenship application, or overseas business filing.",
          highlights: ["Tennessee SOS Specialist", "Embassy & Consulate Ready", "Expedited Same-Day Options"],
        },
        {
          icon: Scroll,
          name: "Estate Planning Documents",
          slug: "estate-planning",
          description: "White-glove handling of trust packages, wills, healthcare directives, advance care plans, and POA packages. Trusted by Nashville-area attorneys, financial planners, and families navigating sensitive transitions. Patience at the bedside, precision on the document.",
          highlights: ["Trust & Healthcare Directive", "Durable & Healthcare POA", "Hospital, Hospice & Home Visits"],
        },
        {
          icon: VideoCamera,
          name: "Remote Online Notarization (RON)",
          slug: "remote-online-notarization",
          description: "Fully Tennessee-compliant audio-visual notarization. Sign legally binding documents from anywhere in the world — perfect for clients abroad, military families, traveling executives, and snowbirds. Encrypted, recorded, and tamper-evident.",
          highlights: ["TN-Compliant Platform", "Audio-Visual Recorded", "International Clients Welcome"],
        },
      ],
    },
    {
      title: "Mobile Notary & Signing Agent",
      description: "Certified, bonded, and ready to come to your home, office, or business — Nashville locally and across the country through our nationwide signing-agent network.",
      icon: PenNib,
      services: [
        {
          icon: PenNib,
          name: "Loan Document Signing",
          slug: "loan-signing",
          description: "Certified Notary Signing Agent (NSA) for refinances, purchases, HELOCs, reverse mortgages, and seller packages. Title companies, escrow officers, and lenders trust India for accurate, on-time signings throughout Davidson and Williamson counties — and across the country through her vetted signing-agent network.",
          highlights: ["Title Company Approved", "Background Checked", "E&O Insured"],
        },
        {
          icon: Stamp,
          name: "General Mobile Notary",
          slug: "general-notary",
          description: "Affidavits, sworn statements, vehicle titles, parental travel consent, custody letters, school documents — any single notarial act performed at your kitchen table, office, or coffee shop.",
          highlights: ["Single & Multi-Document", "Same-Day & Evening Hours", "Anywhere in Nashville"],
        },
        {
          icon: IdentificationCard,
          name: "I-9 Employment Verification",
          slug: "i9-verification",
          description: "On-site I-9 verification for Nashville employers and remote-employee verification across the country. Fully USCIS-compliant. Perfect for HR teams onboarding remote talent.",
          highlights: ["USCIS Compliant", "Remote Hire Verification", "Business Accounts Welcome"],
        },
        {
          icon: Fingerprint,
          name: "Ink Fingerprinting",
          slug: "fingerprinting",
          description: "Traditional ink fingerprint cards for professional licensing, adoption paperwork, immigration filings, and concealed-carry permits. Clean, agency-ready prints rolled by hand — please note: ink only, no electronic / digital live-scan available.",
          highlights: ["Ink Cards Only", "Licensing & Adoption", "Concealed Carry Capture"],
        },
      ],
    },
    {
      title: "Officiant & Specialty Services",
      description: "Beyond the stamp — meaningful moments and specialty services delivered with heart.",
      icon: Heart,
      services: [
        {
          icon: Heart,
          name: "Wedding Officiant Services",
          slug: "wedding-officiant",
          description: "Personalized, heartfelt ceremonies for elopements, courthouse weddings, micro-weddings, vow renewals, and intimate celebrations across Tennessee. From the chapel to the honky-tonk to the hilltop — we'll make your day unforgettable.",
          highlights: ["Custom Ceremonies", "Marriage License Filing", "Available Statewide"],
        },
        {
          icon: FileText,
          name: "Process Serving",
          slug: "process-serving",
          description: "Reliable legal document delivery with court-ready proof of service. Subpoenas, summons, complaints, and small-claims documents served promptly and professionally.",
          highlights: ["Court-Ready Proof", "Same-Day Available", "Statewide Tennessee"],
        },
        {
          icon: HouseLine,
          name: "Hospital, Hospice & Nursing Home Visits",
          slug: "estate-planning",
          description: "Compassionate bedside notarization at Vanderbilt, Saint Thomas, TriStar, and senior-living communities throughout Nashville. Sensitive, dignified service for families during difficult moments.",
          highlights: ["All Nashville Hospitals", "Senior-Living Communities", "Same-Day Emergency"],
        },
      ],
    },
  ];

  return (
    <main data-testid="services-page">
      {/* Header */}
      <section className="bg-[#800000] py-32 pt-40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">{COVERAGE.headline}</span>
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-semibold text-white mb-5">
            A Complete Suite of <span className="italic gold-shimmer">Notary Services</span>
          </h1>
          <p className="text-lg text-white/85 max-w-2xl leading-relaxed">
            From international apostille and bedside estate signings to remote online notarization for clients overseas — every service delivered with MBA-level professionalism and Nashville hospitality.
          </p>
        </div>
      </section>

      {/* Service categories */}
      {serviceCategories.map((category, categoryIndex) => (
        <section
          key={categoryIndex}
          data-testid={`services-category-${categoryIndex}`}
          className={`py-20 ${categoryIndex % 2 === 0 ? 'bg-white' : 'bg-[#FBF8F1]'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category header — no generic "Category N" label */}
            <div className="flex items-center gap-5 mb-14 pb-8 border-b border-[#E8E2D5]">
              <div className="w-16 h-16 rounded-full bg-[#800000] flex items-center justify-center flex-shrink-0">
                <category.icon weight="duotone" size={32} className="text-[#D4AF37]" />
              </div>
              <div>
                <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#800000] leading-tight">
                  {category.title}
                </h2>
                <p className="text-[#5C5C5C] mt-2 text-sm sm:text-base max-w-3xl leading-relaxed">{category.description}</p>
              </div>
            </div>

            {/* Service cards — every card is clickable */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {category.services.map((service, serviceIndex) => (
                <Link
                  key={serviceIndex}
                  to={`/services/${service.slug}`}
                  data-testid={`service-card-${categoryIndex}-${serviceIndex}`}
                  className="group bg-white border border-[#E8E2D5] hover:border-[#800000]/30 p-8 rounded-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 block"
                >
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6 bg-[#800000]/5 group-hover:bg-[#D4AF37]/15 transition-colors">
                    <service.icon weight="duotone" size={28} className="text-[#800000]" />
                  </div>
                  <h3 className="font-serif-display text-2xl font-semibold text-[#800000] mb-4 leading-tight group-hover:text-[#5C0000] transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-[#5C5C5C] mb-6 leading-relaxed text-sm">
                    {service.description}
                  </p>
                  <ul className="space-y-2.5 mb-6">
                    {service.highlights.map((h, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-[#1A1A1A]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#800000]" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <span className="inline-flex items-center gap-2 text-[#800000] group-hover:text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase transition-colors">
                    Explore
                    <ArrowRight weight="bold" size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* 3-tier coverage — replaces the old "All of Middle Tennessee" block */}
      <section className="py-20 bg-[#800000] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-4">Service Coverage</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight">
              {COVERAGE.headline.split(' · ').map((word, i, arr) => (
                <span key={i}>
                  <span className={i === 1 ? 'italic gold-shimmer' : ''}>{word}</span>
                  {i < arr.length - 1 && <span className="text-[#D4AF37] mx-2 sm:mx-3">·</span>}
                </span>
              ))}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {COVERAGE.tiers.map((tier, i) => {
              const Icon = i === 0 ? MapPin : i === 1 ? UsersThree : Globe;
              return (
                <div
                  key={i}
                  data-testid={`coverage-tier-${i}`}
                  className="bg-white/[0.06] backdrop-blur-sm border border-white/15 hover:border-[#D4AF37]/50 rounded-sm p-7 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-5">
                    <Icon weight="duotone" size={24} className="text-[#D4AF37]" />
                  </div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-bold mb-2">
                    {tier.label}
                  </p>
                  <h3 className="font-serif-display text-xl text-white font-semibold mb-2 leading-tight">
                    {tier.area}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {tier.detail}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/booking"
              data-testid="services-book-now-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all px-7 py-4 rounded-sm font-semibold text-sm uppercase tracking-[0.15em]"
            >
              <CalendarCheck weight="bold" size={20} />
              Schedule Service
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#800000] transition-all px-7 py-4 rounded-sm font-semibold text-sm"
            >
              <Phone weight="bold" size={20} />
              Call Now
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
